/**
 * 支払業務 GAS のジョブが終わるのを待つ。
 *
 * GAS の WebApp は 1 回の呼び出しに時間の上限があるため、レポート作成・証憑整理は GAS 側で
 * ジョブとして走らせ、画面は数秒おきに状態を見に行く。
 *
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business ジョブの状態を一定間隔で読み、進みを知らせながら、終われば結果を返し、失敗・時間切れなら理由を投げる
 * @graph-connects none
 */
import type { PaymentJobState } from '@/api/payment-prep';

/** 状態を見に行く間隔 */
/** @graph-connects none */
export const JOB_POLL_INTERVAL_MS = 5_000;

/** 見に行く回数の上限（5 秒 × 180 回 = 15 分） */
/** @graph-connects none */
export const JOB_MAX_POLLS = 180;

/** waitForJob の指定 */
export interface WaitForJobOptions {
  /** いまのジョブの状態を読む */
  fetchJob: () => Promise<PaymentJobState | null>;
  /** 状態を読むたびに呼ばれる */
  onProgress: (job: PaymentJobState) => void;
  /** 待つ。テストでは待たずに返す */
  sleep?: (ms: number) => Promise<void>;
  /** 見に行く回数の上限 */
  maxPolls?: number;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 指定の時間だけ待つ
 * @graph-connects none
 */
function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business ジョブが done になるまで状態を見に行く。error なら GAS の理由を、上限を超えたらシートで確かめるよう投げる
 * @graph-connects none
 */
export async function waitForJob({
  fetchJob,
  onProgress,
  sleep = wait,
  maxPolls = JOB_MAX_POLLS,
}: WaitForJobOptions): Promise<PaymentJobState> {
  for (let poll = 0; poll < maxPolls; poll += 1) {
    await sleep(JOB_POLL_INTERVAL_MS);
    const job = await fetchJob();
    if (job === null) {
      continue;
    }
    onProgress(job);
    if (job.status === 'done') {
      return job;
    }
    if (job.status === 'error') {
      throw new Error(job.error === '' ? 'ジョブが失敗しました' : job.error);
    }
  }
  throw new Error(
    '時間内に終わりませんでした。GAS 側の実行状況はスプレッドシートで確認してください',
  );
}
